'use client'

import Avatar from '@component/avatar'
import DisablePrefetchLink from '@component/disablePrefetchLink'
import ImageBgLoading from '@component/imageBgLoading'
import useEnquiry from '@hooks/useEnquiry'
import houseSearchPageFrom from '@pageFrom/house'
import { favouriteDataLayer } from '@plugin/google/gtm/modules/property'
import { houseDetail } from '@router'
import { global } from '@store/index'
import { joinStr } from '@utils'
import { getComingSoonDateLabel, getHouseAuctionLabel, getHouseOpenhomeLabel } from '@utils/house'
import classNames from 'classnames'
import { get, isFunction, map, slice } from 'lodash-es'
import { useCallback } from 'react'
import { useRecoilValue } from 'recoil'
import CoverSlide from '../coverSlide'
import HouseFacility from '../facility'
import { Favourite } from '../favourite'
import OfficeLine from '../office/line'

export default function ShowCaseListings({
  data,
  showMap = false,
  className = '',
  onMouseEnter,
  onMouseLeave
}) {

  const { mobile } = useRecoilValue(global)
  const mouseEnterHandle = useCallback(event => isFunction(onMouseEnter) && onMouseEnter(data, event), [data, onMouseEnter])
  const mouseLeaveHandle = useCallback(event => isFunction(onMouseLeave) && onMouseLeave(data, event), [data, onMouseLeave])
  function handleClick() {
    houseSearchPageFrom.serpListingClick(data)
  }

  const asideImages = slice(get(data, 'images', []), 1, showMap ? 4 : 5)
  const slideImages = slice([get(data, 'images.0', ''), ...slice(get(data, 'images', []), showMap ? 4 : 5)], 0, 6)
  const agent = get(data, 'agents.0')

  return (
    <DisablePrefetchLink
      href={houseDetail(data)}
      className={classNames('block', { 'shadow-card': showMap }, className)}
      onMouseEnter={mouseEnterHandle}
      onMouseLeave={mouseLeaveHandle}
      onClick={handleClick}
      target={mobile ? '_self' : '_blank'}
    >
      <div className={classNames('grid gap-x-8 grid-cols-1', showMap ? 'lg:grid-cols-[2fr,1fr]' : 'lg:grid-cols-[37.29%,1fr] lg:h-330')}>
        <div className='rounded-t-sm overflow-hidden relative aspect-video lg:rounded-sm lg:aspect-auto'>
          <CoverSlide
            data={data}
            images={slideImages}
            width={554}
            height={331}
            className={classNames({ 'h-full': !showMap })}
            imgClassName='object-cover block aspect-auto'
            alt={data.street}
          >
            <CoverMask data={data} />
          </CoverSlide>
        </div>
        <div className={classNames('grid grid-cols-1 gap-8 min-h-0 p-10 lg:p-0', showMap ? 'h-331' : 'lg:grid-cols-[330px,1fr]')}>
          {
            !showMap && (
              <div className='grid grid-cols-2 gap-8 min-h-0 lg:grid-rows-2 lg:grid-cols-2'>
                {
                  map(asideImages, (src, idx) => (
                    <div key={idx} className={classNames({ 'hidden lg:block': idx >= 2 })}>
                      <ImageBgLoading src={src} width={180} height={160} className='block aspect-video w-full h-full object-cover rounded-sm lg:aspect-auto' alt={`${data.street}_${idx + 1}`}></ImageBgLoading>
                    </div>
                  ))
                }
              </div>
            )
          }
          <div className='rounded-sm overflow-hidden flex flex-col justify-between'>
            <InfoItem showMap={showMap} className='flex-1 flex items-center'>
              <div className='space-y-8 flex flex-col justify-center flex-1 lg:space-y-10'>
                <div className='text-font text-sm line-clamp-1 lg:text-3xl'>{joinStr([data.priceBold, data.priceLight], ' ')}</div>
                <div className='text-font line-clamp-1 lg:line-clamp-2'>
                  <i className='icon icon-address mr-4 sm:hidden'></i>
                  <address className='inline text-sm not-italic lg:text-lg'>{joinStr([data.street, data.suburbName, data.district], ', ')}</address>
                </div>
                <HouseFacility data={data} itemClassName='text-font lg:!text-base' iconClassName='!text-xl lg:!text-1xl'></HouseFacility>
              </div>
              {
                agent && (
                  <div className='lg:hidden'>
                    <Avatar src={agent.icon} width={64} height={64} circle alt={agent.name}></Avatar>
                    <AgentEnquiryButton house={data} agent={agent} />
                  </div>
                )
              }
            </InfoItem>
            <InfoItem showMap={showMap}>
              {agent && <Agent data={agent} house={data} showMap={showMap}></Agent>}
            </InfoItem>
            <OfficeLine data={data} className={classNames(showMap ? 'flex-shrink-0' : '!h-40 mt-10 lg:mt-0 lg:!h-56')} center iconHeight={showMap ? 28 : 32}></OfficeLine>
          </div>
        </div>
      </div>
      {
        showMap && (
          <div className='grid grid-cols-3 gap-8 min-h-0 mt-8'>
            {
              map(asideImages, (src, idx) => (
                <div className='aspect-[217/109]' key={idx}>
                  <ImageBgLoading src={src} width={180} height={160} className='block w-full h-full aspect-auto object-cover rounded-sm' alt={`${data.street}_${idx + 1}`}></ImageBgLoading>
                </div>
              ))
            }
          </div>
        )
      }
    </DisablePrefetchLink>
  )
}

function InfoItem({
  children,
  className,
  showMap
}) {

  return (
    <div className={classNames('first:border-t-0 lg:ml-12 lg:border-t lg:py-10', { 'pr-10': showMap }, className)}>
      {children}
    </div>
  )
}

function Agent({
  data,
  house,
  showMap
}) {

  return (
    <div className='grid-cols-[auto,1fr] gap-x-8 items-center hidden lg:grid'>
      <Avatar src={data.icon} width={90} height={90} circle alt={data.name} className={classNames(showMap ? '!w-46 !h-46' : 'w-90 h-90')}></Avatar>
      <div>
        <div className='text-font text-lg'>{data.name}</div>
        <AgentEnquiryButton house={house} agent={data} />
      </div>
    </div>
  )
}

function AgentEnquiryButton({
  house,
  agent,
}) {
  const { showHouseEnquiry } = useEnquiry()
  return (
    <button
      type='button'
      className='text-orange text-sm sm:text-base sm:font-medium hover:underline hidden lg:block'
      data-noprogress
      onClick={showHouseEnquiry({ house, agent, sendAllAgent: true, signupSource: 'agent_enquiry' })}
    >
      Enquire
      <i className='icon icon-arrow-right !text-sm' />
    </button>
  )
}

function AuctionItem({
  className,
  children
}) {
  return (
    <div className={classNames('flex items-center gap-x-6 before:w-6 before:h-6 before:rounded-half', className)}>{children}</div>
  )
}

function CoverMask({
  data
}) {
  const auctionLabel = getHouseAuctionLabel(data)
  const openhomeLabel = getHouseOpenhomeLabel(data)
  const hasOpenHome = openhomeLabel || auctionLabel
  const comingSoonLabel = getComingSoonDateLabel(data)

  return (
    <div className={classNames('absolute inset-x-0 inset-y-0 z-[3] pointer-events-none', { 'bg-image-mask-showcase': hasOpenHome })}>
      <div className='absolute right-16 top-16 pointer-events-auto'>
        <Favourite data={data} onClick={favouriteDataLayer.iconClick}></Favourite>
      </div>
      {
        hasOpenHome && (
          <div className='absolute left-16 top-16 text-white text-sm font-medium grid gap-y-2 sm:top-auto sm:bottom-16'>
            {/* {
                    auctionLabel && (
                      <AuctionItem className='before:bg-secondary'>Auction {auctionLabel}</AuctionItem>
                    )
                  } */}
            {
              openhomeLabel && (
                <AuctionItem className='before:bg-green'>Open Home {openhomeLabel}</AuctionItem>
              )
            }
            {
              comingSoonLabel && (
                <AuctionItem className='before:bg-secondary'>Coming Soon {comingSoonLabel}</AuctionItem>
              )
            }
          </div>
        )
      }
    </div>
  )
}
